import type { Page } from '../../app/feature';
import type { DrawEvent } from '../../shared/types';
import { esc, q } from '../../shared/dom';
import { getLang, t } from '../../shared/i18n';

const loc = (): string => (getLang() === 'pt' ? 'pt-BR' : 'es-CL');
import { dateTime, fmt, money } from '../../shared/format';
import { getState } from '../../shared/store/store';
import { leadById, pendingPrizes, pozoOf, prizeById } from '../../shared/store/selectors';
import { eligiblePool } from '../../domain/draw-engine';
import { href } from '../../app/router';
import { eventsApi } from '../events/events.api';
import { missionLabel, ruleSentence } from '../missions/missions';

/**
 * Início: o que tem para hoje. Próxima live, pendências que travam a operação e os números
 * da semana. Tudo aponta para a página onde se resolve.
 */
export const overview: Page = {
  id: 'inicio',
  pattern: '/inicio',
  roles: ['ADMIN', 'SUPPORT'],
  nav: { key: 'nav.inicio', icon: '🏠', group: 'ops' },

  template: () => `
<div class="view-head">
  <div>
    <h2 data-i18n="ov.title">Inicio</h2>
    <p id="ov-greeting" class="muted"></p>
  </div>
</div>

<div class="cols">
  <div class="kpi span-3" style="--accent:var(--orange)"><div class="k-label" data-i18n="kpi.players">Jugadores</div><div class="k-val tnum" id="ov-players">0</div><div class="k-sub" id="ov-players-sub">—</div></div>
  <div class="kpi span-3" style="--accent:var(--gold)"><div class="k-label" data-i18n="kpi.cartelas">Cartelas activas</div><div class="k-val tnum" id="ov-cartelas">0</div><div class="k-sub" id="ov-cartelas-sub">—</div></div>
  <div class="kpi span-3" style="--accent:var(--green)"><div class="k-label" data-i18n="ov.events">Sorteos programados</div><div class="k-val tnum" id="ov-events">0</div><div class="k-sub" id="ov-events-sub">—</div></div>
  <div class="kpi span-3" style="--accent:var(--line-2)"><div class="k-label" data-i18n="ov.missions">Misiones activas</div><div class="k-val tnum" id="ov-missions">0</div><div class="k-sub" id="ov-missions-sub">—</div></div>

  <div class="span-8 stack">
    <div class="ov-spot">
      <section class="ovs ovs-next" id="ov-next"></section>
      <section class="ovs ovs-last" id="ov-last"></section>
    </div>
    <div class="panel">
      <div class="panel-head"><h3 data-i18n="ov.byMission">Números emitidos por misión</h3><div class="spacer"></div><a class="btn btn-sm" href="${href('/misiones/configuradas')}" data-i18n="common.edit">Editar</a></div>
      <div class="tw tw-flat"><table id="ov-by-mission"><tbody></tbody></table></div>
    </div>
  </div>

  <div class="span-4 stack ov-side">
    <div class="panel ov-panel">
      <div class="panel-head"><h3 data-i18n="ov.shortcuts">Atajos</h3></div>
      <div class="ov-shortcuts">
        <a class="ov-shortcut tone-orange" href="${href('/sorteos/nuevo')}"><i>🎡</i><span class="sc-txt"><b data-i18n="ov.sc1">Crear un sorteo</b><span data-i18n="ov.sc1d">Fecha, premios y anfitrión</span></span><em>→</em></a>
        <a class="ov-shortcut tone-gold" href="${href('/misiones/configuradas')}"><i>🎯</i><span class="sc-txt"><b data-i18n="ov.sc2">Ajustar misiones</b><span data-i18n="ov.sc2d">Cuánto vale cada acción</span></span><em>→</em></a>
        <a class="ov-shortcut tone-violet" href="${href('/jugadores')}"><i>👥</i><span class="sc-txt"><b data-i18n="ov.sc3">Buscar un jugador</b><span data-i18n="ov.sc3d">Números, actividad, emisión manual</span></span><em>→</em></a>
        <a class="ov-shortcut tone-green" href="${href('/accesos')}"><i>🔑</i><span class="sc-txt"><b data-i18n="ov.sc4">Dar acceso</b><span data-i18n="ov.sc4d">Influencers, soporte, administración</span></span><em>→</em></a>
      </div>
    </div>
    <div class="panel ov-panel ov-todo-panel">
      <div class="panel-head"><h3 data-i18n="ov.todo">Pendientes</h3><span class="ov-count" id="ov-todo-count"></span></div>
      <ul class="ov-todo" id="ov-todo"></ul>
    </div>
  </div>
</div>`,

  mount() {
    // participação em tempo real do próximo sorteio: consulta leve a cada 15 s enquanto o início está aberto
    window.setInterval(() => { void refreshLive(); }, 15_000);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) void refreshLive(); });
  },

  render() {
    const s = getState();
    const hour = new Date().getHours();
    q('#ov-greeting').textContent = t(hour < 12 ? 'ov.morning' : hour < 19 ? 'ov.afternoon' : 'ov.evening');

    /* próxima live */
    const upcoming = s.events.filter(e => e.status === 'scheduled' || e.status === 'live').sort((a, b) => (a.at || '9') .localeCompare(b.at || '9'));
    const next = upcoming[0];
    q('#ov-next').innerHTML = next ? nextCard(next) : `<div class="ovs-kicker">⏭ ${esc(t('ov.next'))}</div><div class="ovs-empty">${esc(t('ov.noNext'))}<a class="btn btn-primary btn-sm" href="${href('/sorteos/nuevo')}">${esc(t('ev.new'))}</a></div>`;
    q('#ov-last').innerHTML = lastCard();
    void refreshLive();

    /* pendências */
    const todo: string[] = [];
    for (const e of upcoming) {
      if (!e.hostName) todo.push(item('🎙', t('ov.noHost').replace('{event}', e.name), '/sorteos/' + e.id));
      if (!pendingPrizes(e.id).length) todo.push(item('🎁', t('ov.noPrizes').replace('{event}', e.name), '/sorteos/' + e.id, 'err'));
    }
    const paused = s.missions.filter(m => !m.active);
    if (paused.length) todo.push(item('⏸', t('ov.pausedMissions').replace('{n}', String(paused.length)), '/misiones/configuradas', 'info'));
    if (!upcoming.length) todo.push(item('📅', t('ov.noEvents'), '/sorteos/nuevo', 'err'));
    q('#ov-todo').innerHTML = todo.join('') || `<li class="ov-ok"><i>✓</i><span>${esc(t('ov.allGood'))}</span></li>`;
    q('#ov-todo-count').textContent = todo.length ? String(todo.length) : '';

    /* números */
    const active = s.cartelas.filter(c => c.status === 'active').length;
    const weekAgo = Date.now() - 7 * 864e5;
    const newPlayers = s.leads.filter(l => l.createdAt >= weekAgo).length;
    const newCartelas = s.cartelas.filter(c => c.createdAt >= weekAgo).length;
    q('#ov-players').textContent = fmt(s.leads.length);
    q('#ov-players-sub').textContent = `+${fmt(newPlayers)} ${t('ov.thisWeek')}`;
    q('#ov-cartelas').textContent = fmt(active);
    q('#ov-cartelas-sub').textContent = `+${fmt(newCartelas)} ${t('ov.thisWeek')}`;
    q('#ov-events').textContent = fmt(upcoming.length);
    q('#ov-events-sub').textContent = next ? `${t('ov.nextOn')} ${next.at || '—'}` : '—';
    const activeMissions = s.missions.filter(m => m.active);
    q('#ov-missions').textContent = fmt(activeMissions.length);
    q('#ov-missions-sub').textContent = `${fmt(paused.length)} ${t('ov.paused')}`;

    const top = [...s.missions].sort((a, b) => b.issuedCount - a.issuedCount).slice(0, 6);
    q('#ov-by-mission tbody').innerHTML = top.map(m => `<tr>
      <td><b>${esc(missionLabel(m))}</b><div class="muted" style="font-size:11.5px">${esc(ruleSentence(m))}</div></td>
      <td class="tnum" style="text-align:right; font-size:18px">${fmt(m.issuedCount)}</td>
    </tr>`).join('') || `<tr><td class="empty">—</td></tr>`;
  }
};

function item(icon: string, text: string, path: string, tone: 'warn' | 'info' | 'err' = 'warn'): string {
  return `<li><a class="tone-${tone}" href="${href(path)}"><i>${icon}</i><span>${esc(text)}</span><em>→</em></a></li>`;
}

/** Próximo sorteio: contagem regressiva, data/hora grandes, o que está em jogo e quem conduz. */
function nextCard(e: DrawEvent): string {
  const s = getState();
  const pend = pendingPrizes(e.id);
  const pool = eligiblePool(s, e);
  const players = new Set(pool.map(c => c.leadId)).size;
  const when = e.at ? new Date(e.at.replace(' ', 'T')) : null;
  const live = e.status === 'live';
  return `
    <div class="ovs-top">
      <div class="ovs-kicker">${live ? `<span class="chip chip-live">● ${esc(t('host.live'))}</span>` : `⏭ ${esc(t('ov.next'))}`}</div>
      <span class="chip ${e.tier === 'mensual' ? 'chip-gold' : 'chip-orange'}">${esc(e.tier === 'mensual' ? t('tier.monthly') : t('tier.weekly'))}</span>
    </div>
    <div class="ovs-main">
      <div class="ovs-date">${when ? `
        <span class="d-dow">${esc(when.toLocaleDateString(loc(), { weekday: 'short' }))}</span>
        <b class="d-day">${when.getDate()}</b>
        <span class="d-mon">${esc(when.toLocaleDateString(loc(), { month: 'short' }))}</span>
        <b class="d-time">${esc(when.toLocaleTimeString(loc(), { hour: '2-digit', minute: '2-digit', hour12: false }))}</b>` : `<b class="d-time">—</b>`}
      </div>
      <div class="ovs-body">
        <b class="ovs-name">${esc(e.name)}</b>
        <div class="ovs-count ${countClass(when)}">${esc(countdown(when, live))}</div>
        <div class="ovs-meta">🎙 ${e.hostName ? esc(e.hostName) : `<span class="warn">${esc(t('ev.noHostYet'))}</span>`}</div>
      </div>
    </div>
    <div class="ovs-prizes">
      <span class="ovs-sub">${esc(t('ov.inPlay'))}</span>
      <div class="ovs-chips">${pend.slice(0, 3).map((p, i) => `<span class="ovs-chip${i === 0 ? ' first' : ''}"><i>${esc(p.emoji || '🎁')}</i>${esc(p.name)}${p.value > 0 ? ` <b>${money(p.value)}</b>` : ''}</span>`).join('')}${pend.length > 3 ? `<span class="ovs-chip more">+${pend.length - 3}</span>` : ''}${pend.length ? '' : `<span class="ovs-chip more">${esc(t('host.needPrizes'))}</span>`}</div>
    </div>
    <div class="ovs-stats ovs-stats-bottom" data-event="${e.id}">
      <div class="live"><b id="ovp-cartelas">${fmt(pool.length)}</b><span>${esc(t('stage.cartelas'))}</span><em id="ovp-cartelas-d"></em></div>
      <div class="live"><b id="ovp-players">${fmt(players)}</b><span>${esc(t('ov.people'))}</span><em id="ovp-players-d"></em></div>
      <div><b>${pend.length}</b><span>${esc(t('host.prizes'))}</span></div>
      <div><b class="gold">${money(pozoOf(e.id))}</b><span>${esc(t('host.pozo'))}</span></div>
    </div>
    <div class="ovs-foot"><span class="ovp-live"><i></i>${esc(t('ov.realtime'))} · <span id="ovp-upd">${esc(t('ov.updatedNow'))}</span></span><a class="ovs-go" href="${href('/sorteos/' + e.id)}">${esc(t('ov.openEvent'))} →</a></div>
`;
}

/** Último sorteio realizado: quem ganhou o quê, com o número em fichas. */
function lastCard(): string {
  const s = getState();
  const done = s.events.filter(e => e.status === 'done');
  const lastAt = (id: string) => Math.max(0, ...s.draws.filter(d => d.eventId === id).map(d => d.at));
  const last = done.filter(e => lastAt(e.id) > 0).sort((a, b) => lastAt(b.id) - lastAt(a.id))[0];
  if (!last) return `<div class="ovs-kicker">🏆 ${esc(t('ov.last'))}</div><div class="ovs-empty">${esc(t('ov.noLast'))}</div>`;
  const ds = s.draws.filter(d => d.eventId === last.id).sort((a, b) => a.at - b.at);
  const delivered = ds.reduce((sum, d) => sum + (prizeById(d.prizeId)?.value || 0), 0);
  const rows = ds.slice(0, 2).map((d, i) => {
    const p = prizeById(d.prizeId);
    return `<div class="ovl-row${i === 0 ? ' first' : ''}">
      <span class="ovl-num">${d.number.split('').map(ch => `<b>${ch}</b>`).join('')}</span>
      <span class="ovl-who"><b>${esc(leadById(d.leadId)?.name ?? '—')}</b><small>${esc(p?.emoji || '🎁')} ${esc(p?.name ?? '—')}${p && p.value > 0 ? ` · <em>${money(p.value)}</em>` : ''}</small></span>
    </div>`;
  }).join('');
  const more = ds.length > 2 ? `<div class="ovl-more">+${ds.length - 2} ${esc(t('host.prizes'))}</div>` : '';
  return `
    <div class="ovs-top">
      <div class="ovs-kicker">🏆 ${esc(t('ov.last'))}</div>
      <span class="chip chip-done">✓ ${esc(t('evst.done'))}</span>
    </div>
    <b class="ovs-name">${esc(last.name)}</b>
    <div class="ovs-meta">${esc(dateTime(lastAt(last.id)))} · 🎙 ${esc(last.hostName || t('ev.nohost'))}</div>
    <div class="ovs-stats">
      <div><b>${last.poolCartelas != null ? fmt(last.poolCartelas) : fmt(ds[0]?.poolSize ?? 0)}</b><span>${esc(t('stage.cartelas'))}</span></div>
      <div><b>${last.poolPlayers != null ? fmt(last.poolPlayers) : '—'}</b><span>${esc(t('ov.people'))}</span></div>
      <div><b>${ds.length}</b><span>${esc(t('host.prizes'))}</span></div>
      <div><b class="gold">${money(delivered)}</b><span>${esc(t('ev.deliveredPozo'))}</span></div>
    </div>
    <div class="ovl-rows">${rows}${more}</div>
    <div class="ovs-foot"><span class="ovp-live done">✓ ${esc(t('ov.participated'))}</span><a class="ovs-go" href="${href('/sorteos/' + last.id)}">${esc(t('ov.seeResult'))} →</a></div>`;
}

function countdown(when: Date | null, live: boolean): string {
  if (live) return t('ov.liveNow');
  if (!when) return t('ov.noDate');
  const ms = when.getTime() - Date.now();
  if (ms <= 0) return t('host.startable');
  const min = Math.floor(ms / 60_000), h = Math.floor(min / 60), d = Math.floor(h / 24);
  if (min < 60) return t('host.inMinutes').replace('{m}', String(min));
  if (h < 24) return t('host.inHours').replace('{h}', String(h)).replace('{m}', String(min % 60));
  return t('host.inDays').replace('{n}', String(d));
}

function countClass(when: Date | null): string {
  if (!when) return '';
  const ms = when.getTime() - Date.now();
  return ms <= 0 ? 'go' : ms < 3 * 3600_000 ? 'soon' : '';
}

let lastLive: { id: string; cartelas: number; players: number; at: number } | null = null;

/** Atualiza as contagens do próximo sorteio sem redesenhar a página; mostra o quanto entrou desde a última leitura. */
async function refreshLive(): Promise<void> {
  const box = document.querySelector<HTMLElement>('#ov-next .ovs-stats[data-event]');
  if (!box || document.hidden || !location.hash.startsWith('#/inicio')) return;
  const id = box.dataset.event as string;
  try {
    const r = await eventsApi.poolSummary(id);
    const prev = lastLive && lastLive.id === id ? lastLive : null;
    setNum('#ovp-cartelas', '#ovp-cartelas-d', r.cartelas, prev?.cartelas);
    setNum('#ovp-players', '#ovp-players-d', r.players, prev?.players);
    lastLive = { id, cartelas: r.cartelas, players: r.players, at: Date.now() };
    const upd = document.querySelector('#ovp-upd');
    if (upd) upd.textContent = t('ov.updatedAt').replace('{t}', new Date().toLocaleTimeString(loc(), { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
  } catch { /* sem rede: mantém o último valor */ }
}

function setNum(sel: string, deltaSel: string, value: number, before: number | undefined): void {
  const el = document.querySelector<HTMLElement>(sel);
  const d = document.querySelector<HTMLElement>(deltaSel);
  if (!el) return;
  el.textContent = fmt(value);
  if (before !== undefined && value !== before) {
    const diff = value - before;
    if (d) { d.textContent = diff > 0 ? `+${fmt(diff)}` : fmt(diff); d.classList.remove('bump'); void d.offsetWidth; d.classList.add('bump'); }
    el.classList.remove('flash'); void el.offsetWidth; el.classList.add('flash');
  }
}
